import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, FileText, Mic, Play, Sparkles, Bookmark, BookMarked, MessageCircle, ArrowLeft, ExternalLink, ShoppingCart, Headphones, Library, Play as PlayIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { REC, COMMUNITY } from "@/constants/testIds/app";

const iconOf = {
  book: BookOpen,
  article: FileText,
  podcast: Mic,
  video: Play,
  exercise: Sparkles,
};

const linkIconOf = {
  read: ExternalLink,
  buy: ShoppingCart,
  audio: Headphones,
  library: Library,
  listen: Headphones,
  watch: PlayIcon,
};

const FALLBACK_COVER = "https://images.unsplash.com/photo-1755541608494-5c02cf56e1f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHx2aW50YWdlJTIwYm9vayUyMGNvdmVyJTIwYWVzdGhldGljfGVufDB8fHx8MTc4NjAyNjI0OHww&ixlib=rb-4.1.0&q=85";

export default function Recommendations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedIdx, setSavedIdx] = useState(new Map());
  const [reviewIdx, setReviewIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/recommend/${id}`);
        setRec(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const save = async (idx, status) => {
    try {
      await api.post("/library", { recommendation_id: id, item_index: idx, status });
      setSavedIdx((s) => new Map(s).set(idx, status));
      toast.success(status === "to-read" ? "Added to your reading list" : "Saved to your library");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <div data-testid={REC.loading} className="max-w-3xl">
        <p className="font-serif-display italic text-[#2C2520]/60">Setting the picks on the table…</p>
      </div>
    );
  }
  if (!rec) return <p className="text-[#2C2520]/70">Nothing here.</p>;

  return (
    <div data-testid={REC.container} className="space-y-10">
      <button
        onClick={() => navigate("/home")}
        className="inline-flex items-center gap-1.5 text-sm text-[#2C2520]/70 hover:text-[#2C2520]"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Home
      </button>

      <section>
        <div className="label-caps mb-4">Your picks</div>
        <p
          data-testid={REC.empathicNote}
          className="font-serif-display italic text-2xl sm:text-3xl text-[#2C2520] leading-snug max-w-3xl"
        >
          "{rec.empathic_note}"
        </p>
        {rec.themes?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            {rec.themes.map((t, i) => (
              <span
                key={i}
                data-testid={REC.themeChip(i)}
                className="px-3 py-1 rounded-full bg-[#F5F2EA] border border-[#E8E3D5] text-xs tracking-wider uppercase text-[#4A5D4E]"
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rec.recommendations.map((item, i) => {
          const Icon = iconOf[item.type] || BookOpen;
          const isBook = item.type === "book";
          const cover = item.cover_url || (isBook ? FALLBACK_COVER : null);
          return (
            <article
              key={i}
              data-testid={REC.card(i)}
              className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] overflow-hidden flex flex-col hover:shadow-[0_8px_24px_rgba(44,37,32,0.08)] transition-shadow duration-300"
            >
              {isBook && cover ? (
                <div className="aspect-[3/4] bg-[#EDE8DB] overflow-hidden">
                  <img src={cover} alt={item.title} className="w-full h-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                </div>
              ) : (
                <div className="aspect-[16/9] bg-[#EDE8DB] flex items-center justify-center">
                  <Icon className="w-10 h-10 text-[#4A5D4E]/60" strokeWidth={1.5} />
                </div>
              )}
              <div className="p-5 flex-1 flex flex-col gap-3">
                <div className="label-caps">{item.type}</div>
                <h3 className="font-serif-display text-xl text-[#2C2520] leading-snug">{item.title}</h3>
                <p className="text-sm text-[#2C2520]/65">{item.author}</p>
                <p className="text-sm text-[#2C2520]/80 leading-relaxed italic">"{item.reason}"</p>
                {item.preview && (
                  <blockquote className="text-sm text-[#2C2520]/85 leading-relaxed font-serif-display italic border-l-2 border-[#C7BCA1] pl-3 pr-2 py-1 bg-[#FDFBF7]/60 rounded-r-[8px]">
                    "{item.preview}"
                  </blockquote>
                )}
                {item.instructions && (
                  <p className="text-sm text-[#2C2520]/80 leading-relaxed border-l-2 border-[#4A5D4E] pl-3">
                    {item.instructions}
                  </p>
                )}
                {item.access_links?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {item.access_links.map((lnk, li) => {
                      const LIcon = linkIconOf[lnk.kind] || ExternalLink;
                      const primary = li === 0;
                      return (
                        <a
                          key={li}
                          href={lnk.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid={`rec-access-${i}-${lnk.kind}`}
                          className={
                            "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs transition-colors " +
                            (primary
                              ? "bg-[#4A5D4E] text-white hover:bg-[#3D4D40]"
                              : "bg-[#FDFBF7] border border-[#E8E3D5] text-[#2C2520] hover:bg-[#EDE8DB]")
                          }
                        >
                          <LIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {lnk.label}
                        </a>
                      );
                    })}
                  </div>
                )}
                <div className="mt-auto pt-3 flex gap-2 flex-wrap">
                  <button
                    data-testid={REC.saveBtn(i)}
                    onClick={() => save(i, "reading")}
                    disabled={savedIdx.has(i)}
                    className="flex-1 min-w-[110px] px-3 py-2 rounded-[10px] bg-[#4A5D4E] text-white text-sm hover:bg-[#3D4D40] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                  >
                    <Bookmark className="w-4 h-4" strokeWidth={1.5} />
                    {savedIdx.get(i) === "reading" ? "Saved" : "Save"}
                  </button>
                  {isBook && (
                    <button
                      data-testid={REC.laterBtn(i)}
                      onClick={() => save(i, "to-read")}
                      disabled={savedIdx.get(i) === "to-read"}
                      className="flex-1 min-w-[110px] px-3 py-2 rounded-[10px] border border-[#4A5D4E] text-[#4A5D4E] text-sm hover:bg-[#EDE8DB] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                    >
                      <BookMarked className="w-4 h-4" strokeWidth={1.5} />
                      {savedIdx.get(i) === "to-read" ? "On list" : "For later"}
                    </button>
                  )}
                  <button
                    data-testid={REC.reviewBtn(i)}
                    onClick={() => setReviewIdx(i)}
                    className="px-3 py-2 rounded-[10px] border border-[#E8E3D5] text-sm text-[#2C2520] hover:bg-[#EDE8DB] inline-flex items-center gap-1.5"
                    title="Share a reflection"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#4A5D4E] underline underline-offset-4"
                  >
                    Source link
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </section>

      <div className="flex flex-wrap gap-3">
        <Link
          to="/library"
          className="px-5 py-2.5 rounded-[12px] border border-[#E8E3D5] text-sm text-[#2C2520] hover:bg-[#F5F2EA]"
        >
          Go to library
        </Link>
        <Link
          to="/community"
          className="px-5 py-2.5 rounded-[12px] border border-[#E8E3D5] text-sm text-[#2C2520] hover:bg-[#F5F2EA]"
        >
          Read community reflections
        </Link>
      </div>

      <p className="text-xs text-[#2C2520]/55 leading-relaxed max-w-2xl">
        Some access links are affiliate links, so a small commission may support the app when you buy through them.
        Free options like Open Library and your local library are always included.{" "}
        <Link to="/disclosure" className="text-[#4A5D4E] underline underline-offset-4">Learn more</Link>.
      </p>

      {reviewIdx !== null && (
        <ReviewModal
          item={rec.recommendations[reviewIdx]}
          onClose={() => setReviewIdx(null)}
        />
      )}
    </div>
  );
}

function ReviewModal({ item, onClose }) {
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 4) {
      toast.error("A few words, please");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reviews", {
        resource_title: item.title,
        resource_type: item.type,
        rating,
        body,
      });
      toast.success("Reflection shared");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#2C2520]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-[#FDFBF7] border border-[#E8E3D5] rounded-[12px] p-8 max-w-md w-full shadow-[0_16px_40px_rgba(44,37,32,0.15)]" onClick={(e) => e.stopPropagation()}>
        <div className="label-caps mb-3">Share a reflection</div>
        <h3 className="font-serif-display text-2xl text-[#2C2520]">{item.title}</h3>
        <p className="text-sm text-[#2C2520]/60 mt-1">{item.author}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <div className="label-caps mb-2">Rating</div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={COMMUNITY.composerRating(n)}
                  onClick={() => setRating(n)}
                  className={
                    "w-10 h-10 rounded-[10px] border transition-colors " +
                    (n <= rating ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "bg-[#F5F2EA] text-[#2C2520] border-[#E8E3D5]")
                  }
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-caps mb-2">What did it mean to you?</div>
            <textarea
              data-testid={COMMUNITY.composerBody}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              className="w-full p-3 rounded-[12px] bg-[#F5F2EA] border border-[#E8E3D5] focus:outline-none focus:border-[#4A5D4E]"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-[10px] text-sm text-[#2C2520]/70 hover:bg-[#F5F2EA]">
              Cancel
            </button>
            <button
              type="submit"
              data-testid={COMMUNITY.composerSubmit}
              disabled={busy}
              className="px-4 py-2 rounded-[10px] bg-[#4A5D4E] text-white text-sm hover:bg-[#3D4D40] disabled:opacity-60"
            >
              {busy ? "Sharing…" : "Share"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
